import { motion } from "framer-motion";

const photos = [
  {
    id: "reception",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/rnsc6use_IMG_0478.webp",
    alt: "Apex Dental Care reception and waiting lounge",
    caption: "The Reception",
    tag: "01",
    span: "col-span-12 lg:col-span-7",
    aspect: "aspect-[4/3]",
    color: "#7A5638",
  },
  {
    id: "logo-backlit",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/wra7t9o3_IMG_0481.webp",
    alt: "Backlit Apex Dental Care logo on herringbone wood panel",
    caption: "The Emblem",
    tag: "02",
    span: "col-span-12 lg:col-span-5",
    aspect: "aspect-[4/5]",
    color: "#1E5AA8",
  },
  {
    id: "doctors-plates",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/97wrmkp9_IMG_0474.webp",
    alt: "Acrylic LED name plates of Dr. Leona Arun and Dr. Arun Paul",
    caption: "The Practitioners",
    tag: "03",
    span: "col-span-12 md:col-span-6 lg:col-span-4",
    aspect: "aspect-square",
    color: "#B8944E",
  },
  {
    id: "front-desk",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/so2baf6b_IMG_0473.webp",
    alt: "Front desk view with backlit Apex logo",
    caption: "The Front Desk",
    tag: "04",
    span: "col-span-12 md:col-span-6 lg:col-span-4",
    aspect: "aspect-square",
    color: "#2D6BB4",
  },
  {
    id: "chair-room-1",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/i2949xk8_IMG_0475.webp",
    alt: "Dental chair treatment room with round accent light",
    caption: "The Chair Room",
    tag: "05",
    span: "col-span-12 lg:col-span-4",
    aspect: "aspect-square",
    color: "#C8955C",
  },
  {
    id: "xray",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/xo2clue7_IMG_0476.webp",
    alt: "Digital X-ray unit against wood-slatted wall",
    caption: "Diagnostic Imaging",
    tag: "06",
    span: "col-span-12 md:col-span-6 lg:col-span-5",
    aspect: "aspect-square",
    rotate: 90,
    color: "#8B6942",
  },
  {
    id: "chair-room-2",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/mbub06u6_IMG_0479.webp",
    alt: "Treatment room with dental chair and consulting desk",
    caption: "The Consult",
    tag: "07",
    span: "col-span-12 md:col-span-6 lg:col-span-7",
    aspect: "aspect-[3/2]",
    color: "#8A3A2A",
  },
  {
    id: "waiting",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/ufktl1fk_IMG_0480.webp",
    alt: "Waiting seats and window looking onto Edamuttam street",
    caption: "The Waiting Bench",
    tag: "08",
    span: "col-span-12 lg:col-span-7",
    aspect: "aspect-[4/3]",
    color: "#B8302A",
  },
  {
    id: "portrait",
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_root-canal-experts-2/artifacts/tt9084qz_IMG_0477.webp",
    alt: "Framed Sacred Heart portrait, spot-lit on the slat wall",
    caption: "The Great Physician is always in.",
    tag: "09",
    span: "col-span-12 lg:col-span-5",
    aspect: "aspect-square",
    rotate: 90,
    color: "#C8955C",
  },
];

function Photo({ p, i }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.06, ease: [0.76, 0, 0.24, 1] }}
      className={`group relative ${p.span}`}
      data-testid={`gallery-photo-${p.id}`}
    >
      <div className={`relative w-full ${p.aspect} overflow-hidden bg-bone`}>
        <img
          src={p.src}
          alt={p.alt}
          loading="lazy"
          style={p.rotate ? { transform: `rotate(${p.rotate}deg)`, transformOrigin: "center" } : undefined}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-ink/5 group-hover:bg-ink/0 transition-colors duration-700" />
      </div>
      <figcaption className="mt-4 flex items-baseline justify-between">
        <span className="font-serif italic text-lg md:text-xl" style={{ color: p.color }}>{p.caption}</span>
        <span className="eyebrow" style={{ color: p.color, opacity: 0.7 }}>{p.tag}</span>
      </figcaption>
    </motion.figure>
  );
}

export default function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-28 md:py-40 bg-porcelain border-b border-bone"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-ink" />
              <span className="eyebrow">Inside the Clinic</span>
              <span className="font-malayalam text-[13px] text-ink/60 leading-none">ക്ലിനിക്</span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
              data-testid="gallery-heading"
              style={{ color: "#4A3520" }}
            >
              A quiet space, <br />
              <span className="italic" style={{ color: "#B8944E" }}>carefully composed.</span>
            </motion.h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 md:pt-4">
            <p className="text-ink/70 text-base md:text-lg leading-relaxed max-w-[46ch] font-light">
              Nine details from the Edamuttam clinic — the light, the wood, the equipment,
              the small human touches — before you arrive.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8 lg:gap-10">
          {photos.map((p, i) => (
            <Photo key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
